import React from 'react';

import { FormModal } from '../../records';
import repairService from '../../../services/repairService';
import type { FirearmCondition, FirearmRepair } from '../../../types/domain';

interface Props {
  repair: FirearmRepair;
  onClose: () => void;
  onCompleted: (repair: FirearmRepair) => void;
}

const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const CompleteRepairDialog: React.FC<Props> = ({ repair, onClose, onCompleted }) => {
  const [completionDate, setCompletionDate] = React.useState('');
  const [condition, setCondition] = React.useState<FirearmCondition>('serviceable');
  const [workPerformed, setWorkPerformed] = React.useState('');
  const [photos, setPhotos] = React.useState<File[]>([]);
  const [confirmed, setConfirmed] = React.useState(false);
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const previews = React.useMemo(
    () => photos.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [photos],
  );

  React.useEffect(
    () => () => previews.forEach(({ url }) => URL.revokeObjectURL(url)),
    [previews],
  );

  const handleFiles = (files: FileList | null) => {
    const selected = Array.from(files || []);
    if (selected.length > 5) {
      setError('Upload at most 5 repair photos.');
      return;
    }
    const invalidType = selected.find((file) => !ACCEPTED_TYPES.has(file.type));
    if (invalidType) {
      setError('Only JPEG, PNG, and WebP repair photos are allowed.');
      return;
    }
    const tooLarge = selected.find((file) => file.size > MAX_FILE_SIZE);
    if (tooLarge) {
      setError('Each repair photo must be 5 MB or smaller.');
      return;
    }
    setError('');
    setPhotos(selected);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!completionDate || !workPerformed.trim() || !confirmed) {
      setError('Completion date, repair remarks, and confirmation are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const updated = await repairService.completeRepair(repair.id, {
        completionDate,
        postRepairCondition: condition,
        workPerformed: workPerformed.trim(),
        photos,
      });
      onCompleted(updated);
    } catch (requestError: any) {
      const data = requestError?.response?.data;
      setError(
        data?.detail
        || Object.values(data || {}).flat().join(' ')
        || 'Repair could not be completed.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open
      title="Complete Repair"
      description={repair.firearm_serial_number}
      onClose={onClose}
    >
      <form className="inspector-form" onSubmit={submit}>
        <label>
          Repair Completion Date
          <input
            type="date"
            value={completionDate}
            max={new Date().toLocaleDateString('en-CA')}
            onChange={(event) => setCompletionDate(event.target.value)}
            required
          />
        </label>
        <label>
          Post-Repair Condition
          <select
            value={condition}
            onChange={(event) => setCondition(event.target.value as FirearmCondition)}
          >
            <option value="serviceable">Serviceable — return to Available</option>
            <option value="defective">Defective — keep repair open</option>
            <option value="unserviceable">Unserviceable — mark Inactive</option>
          </select>
        </label>
        <label className="full-width">
          Repair Remarks / Work Performed
          <textarea
            value={workPerformed}
            onChange={(event) => setWorkPerformed(event.target.value)}
            rows={5}
            required
          />
        </label>
        <label className="full-width">
          Photo Evidence (Optional)
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(event) => handleFiles(event.target.files)}
          />
        </label>
        {previews.length ? (
          <div className="repair-photo-preview-grid full-width">
            {previews.map(({ file, url }) => (
              <figure key={`${file.name}-${file.lastModified}`}>
                <img src={url} alt={`Selected repair evidence ${file.name}`} />
                <figcaption>{file.name}</figcaption>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setPhotos((current) => current.filter((item) => item !== file))}
                >
                  Remove
                </button>
              </figure>
            ))}
          </div>
        ) : null}
        <label className="full-width">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          I confirm this repair outcome and resulting firearm status.
        </label>
        {error ? <div className="field-error full-width" role="alert">{error}</div> : null}
        <div className="form-actions full-width">
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : condition === 'defective' ? 'Record Follow-up' : 'Complete Repair'}
          </button>
          <button className="btn btn-secondary" type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default CompleteRepairDialog;
